'use client';
import Spinner from '@hyphengroup/cms-components/components/Spinner/Spinner';
import {
  Popover,
  PopoverContent,
  PopoverHandler,
} from '@material-tailwind/react';
import staticText from 'car-insurance/assets/static-content/dashboard.json';
import classNames from 'classnames';
import { MaterialIcon } from 'components/modulab/MaterialIcon';
import { useLocaleSpecificClient } from 'hooks/useSpecificClient';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface OptionProps {
  value: string;
  label: string;
}

export interface CISelectProps {
  placeholder?: string;
  options: OptionProps[];
  prefixIcon?: React.ReactNode;
  onChange?: (value: string) => void;
  value?: string;
  border?: boolean;
  search?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  labelClassName?: string;
  containerClassName?: string;
  showBodyShadow?: boolean;
  showDropdownArrow?: boolean;
  openedContainerClassName?: string;
  dropdownClassName?: string;
  disabledClassName?: string;
  popoverClassName?: string;
}

export const CISelect: React.FC<CISelectProps> = ({
  prefixIcon,
  options,
  value,
  placeholder,
  border,
  onChange,
  search,
  disabled = false,
  isLoading = false,
  containerClassName,
  openedContainerClassName,
  labelClassName,
  showBodyShadow = false,
  showDropdownArrow = true,
  dropdownClassName = '',
  disabledClassName = '',
  popoverClassName = '',
}) => {
  const [isPopoverOpened, setIsPopoverOpened] = useState<boolean>(false);
  const [optionsToDisplay, setOptionsToDisplay] =
    useState<OptionProps[]>(options);
  const [selectedOptionLabel, setSelectedOptionLabel] = useState<
    string | undefined
  >('');
  const [searchInputValue, setSearchInputValue] = useState<string>('');

  const popoverHandlerRef = useRef<HTMLDivElement | null>(null);
  const popoverSearchFieldRef = useRef<HTMLInputElement | null>(null);

  const disabledStyles =
    disabledClassName || '!cursor-default pointer-events-none !bg-gray-500';

  const togglePopover = () => {
    if (!disabled && !isLoading) {
      setIsPopoverOpened((prev) => !prev);
    }
  };

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const searchValue = event.target.value;

    const filteredOptions = options.filter(
      (option) =>
        option.value.toLowerCase().includes(searchValue.toLowerCase()) ||
        option.label.toLowerCase().includes(searchValue.toLowerCase()),
    );

    setSearchInputValue(searchValue);
    setOptionsToDisplay(filteredOptions);
  };

  const handleChange = (option: OptionProps) => {
    setSelectedOptionLabel(option.label);
    onChange?.(option.value);
    setIsPopoverOpened(false);
  };

  const findDefaultValueLabel = useCallback(() => {
    const selectedOption = options.filter(
      (option) => option.value === value,
    )[0];
    if (selectedOption) {
      setSelectedOptionLabel(selectedOption.label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const focusPopoverSearchField = useCallback(() => {
    if (isPopoverOpened && popoverSearchFieldRef.current) {
      setTimeout(() => {
        popoverSearchFieldRef.current?.focus();
      }, 0);
    }
  }, [isPopoverOpened]);

  const popoverWidth = popoverHandlerRef.current?.clientWidth
    ? popoverHandlerRef.current.clientWidth
    : 200;

  useEffect(() => {
    findDefaultValueLabel();
  }, [findDefaultValueLabel]);

  useEffect(() => {
    if (!value && selectedOptionLabel) {
      setSelectedOptionLabel(undefined);
    }
  }, [value, selectedOptionLabel, setSelectedOptionLabel]);

  const inputPlaceholder = useLocaleSpecificClient({
    ...staticText.policySearch.select.searchField,
  });

  useEffect(() => {
    setSearchInputValue('');
    setOptionsToDisplay(options);
  }, [options]);

  useEffect(() => {
    focusPopoverSearchField();
  }, [focusPopoverSearchField]);

  return (
    <Popover
      open={isPopoverOpened}
      handler={togglePopover}
      placement="bottom-start"
    >
      {isPopoverOpened &&
        showBodyShadow &&
        createPortal(
          <div className="fixed top-0 bg-black/30 size-full" />,
          document.body,
        )}
      <PopoverHandler ref={popoverHandlerRef}>
        <div className="size-full">
          <button
            className={classNames(
              'bg-white max-h-11 rounded-xl font-medium border p-2 border-gray-400 flex items-center flex-row justify-between sm:px-4 lg:px-4 md:px-2 size-full overflow-hidden',
              {
                '!border-transparent': !border,
                [`${disabledStyles}`]: disabled,
                '!cursor-default pointer-events-none': isLoading,
              },
              {
                [`${containerClassName}`]: Boolean(containerClassName),
                [`${openedContainerClassName}`]:
                  Boolean(openedContainerClassName) && isPopoverOpened,
              },
            )}
            type="button"
          >
            <div
              className={classNames(
                'text-start flex items-center gap-2 w-full',
                { '!w-[90%]': showDropdownArrow },
              )}
            >
              {prefixIcon}
              <div
                className={classNames(
                  'text-gray-900 font-medium lg:text-lg sm:text-base md:text-sm w-full truncate',
                  {
                    [`${labelClassName}`]: Boolean(labelClassName),
                    ['!text-gray-400']: Boolean(
                      !selectedOptionLabel && placeholder,
                    ),
                  },
                )}
              >
                {selectedOptionLabel || placeholder}
              </div>
            </div>
            {showDropdownArrow &&
              (isLoading ? (
                <Spinner className="h-2/3" />
              ) : (
                <MaterialIcon
                  icon="expand_more"
                  className={classNames(
                    `text-xl text-gray-600 overflow-visible ${dropdownClassName}`,
                    { 'rotate-180': isPopoverOpened },
                  )}
                />
              ))}
          </button>
        </div>
      </PopoverHandler>
      <PopoverContent
        // eslint-disable-next-line tailwindcss/no-arbitrary-value
        className={classNames(
          'z-dropdown border border-gray-600 shadow-xl rounded-lg md:rounded-xl p-0 overflow-hidden',
          { [`${popoverClassName}`]: Boolean(popoverClassName) },
        )}
      >
        <div
          className="flex flex-col max-h-96 w-full"
          style={{ width: popoverWidth }}
        >
          {search && (
            <div className="flex items-center md:px-2 lg:px-4 md:gap-1 lg:gap-2 border-b border-b-gray-300 rounded-none">
              <div className="flex-none size-6">
                <MaterialIcon icon="search" />
              </div>
              <input
                className="border-none outline-none text-base font-medium text-gray-600 pl-0 w-full py-2"
                ref={popoverSearchFieldRef}
                placeholder={inputPlaceholder}
                value={searchInputValue}
                onChange={handleSearchInputChange}
              />
            </div>
          )}
          <div className="py-2 px-4 flex flex-col overflow-y-auto max-h-70">
            {optionsToDisplay.map((option, index) => (
              <div
                onClick={() => handleChange(option)}
                role="button"
                tabIndex={index}
                className={classNames(
                  'flex justify-between items-center hover:bg-gray-200 cursor-pointer sm:p-3 md:p-2 lg:p-3 rounded-lg',
                  {
                    'bg-gray-200': option.label === selectedOptionLabel,
                  },
                )}
                // eslint-disable-next-line react/no-array-index-key
                key={`${option.value}_${index}`}
                data-value={option.value}
              >
                <p className="font-medium text-lg text-gray-900">
                  {option.label}
                </p>
                {option.label === selectedOptionLabel && (
                  <div className="size-6">
                    <MaterialIcon icon="check" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
